import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Check, Minus, Plus, ChefHat } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

const C = {
  bg: '#FDFBF7', cream: '#F5F0E8', card: '#FFFFFF', border: '#E8E3DB',
  text: '#1A1A18', textSec: '#6A6560', muted: '#9A9590',
  accent: '#C67A5C', green: '#5A8C6A', gold: '#E5A84B',
};

const PORTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2];

function getAutoMealType() {
  const h = new Date().getHours();
  if (h < 10) return 'breakfast';
  if (h < 15) return 'lunch';
  if (h < 19) return 'dinner';
  return 'snack';
}

function gradeFromCalories(cal) {
  if (cal <= 400) return 'A';
  if (cal <= 550) return 'B';
  if (cal <= 700) return 'C';
  if (cal <= 900) return 'D';
  return 'F';
}

const GRADE_COLORS = { A: '#5A8C6A', B: '#5A8C6A', C: '#E5A84B', D: '#C67A5C', F: '#D4736A' };

/**
 * SmartLogConfirm — Pre-populated meal logging confirmation screen.
 * Shows meal name, ingredients, macros, portion selector, meal type selector.
 * Props:
 *   meal: { name, calories, protein, carbs, fat, items?, ingredients?, why?, recipe_id? }
 *   onClose: () => void  (go back to previous screen)
 */
export default function SmartLogConfirm({ meal, onClose }) {
  const navigate = useNavigate();
  const { authHeaders } = useAuth();
  const [portion, setPortion] = useState(1);
  const [mealType, setMealType] = useState(getAutoMealType());
  const [logging, setLogging] = useState(false);
  const [logged, setLogged] = useState(false);
  const [excludedItems, setExcludedItems] = useState(new Set());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = meal.items || meal.ingredients || [];

  // Calculate macros with portion multiplier and excluded items
  const adjusted = useMemo(() => {
    let activeItems = items.filter((_, i) => !excludedItems.has(i));
    if (activeItems.length > 0) {
      const cal = activeItems.reduce((s, it) => s + (it.calories || 0), 0) * portion;
      const prot = activeItems.reduce((s, it) => s + (it.protein || 0), 0) * portion;
      const carbs = activeItems.reduce((s, it) => s + (it.carbs || 0), 0) * portion;
      const fat = activeItems.reduce((s, it) => s + (it.fat || 0), 0) * portion;
      return { calories: Math.round(cal), protein: Math.round(prot), carbs: Math.round(carbs), fat: Math.round(fat) };
    }
    return {
      calories: Math.round((meal.calories || 0) * portion),
      protein: Math.round((meal.protein || 0) * portion),
      carbs: Math.round((meal.carbs || 0) * portion),
      fat: Math.round((meal.fat || 0) * portion),
    };
  }, [portion, excludedItems, items, meal]);

  const grade = gradeFromCalories(adjusted.calories);

  const handleConfirm = async () => {
    setLogging(true);
    try {
      const activeItems = items.filter((_, i) => !excludedItems.has(i)).map(it => ({
        name: it.name,
        quantity: it.quantity || '1 serving',
        calories: Math.round((it.calories || 0) * portion),
        protein: Math.round((it.protein || 0) * portion),
        carbs: Math.round((it.carbs || 0) * portion),
        fat: Math.round((it.fat || 0) * portion),
      }));

      await axios.post(`${API}/meals/log-prefill`, {
        name: meal.name,
        meal_type: mealType,
        items: activeItems.length > 0 ? activeItems : [{
          name: meal.name,
          quantity: `${portion}x serving`,
          calories: adjusted.calories,
          protein: adjusted.protein,
          carbs: adjusted.carbs,
          fat: adjusted.fat,
        }],
        totals: adjusted,
        source: 'smart_prefill',
        portion_multiplier: portion,
      }, authHeaders());

      setLogged(true);
      toast.success('Meal logged!');
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err) {
      toast.error('Failed to log meal');
    }
    setLogging(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, paddingBottom: 100 }} data-testid="smart-log-confirm">
      {/* Header */}
      <header style={{ background: C.bg, position: 'sticky', top: 0, zIndex: 50, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', height: 56, gap: 12 }}>
          <button onClick={onClose} style={{ padding: 8, background: 'none', border: 'none', cursor: 'pointer', color: C.text }} data-testid="smart-log-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 400, color: C.text, margin: 0, flex: 1 }}>Log Meal</h1>
        </div>
      </header>

      <main style={{ maxWidth: 600, margin: '0 auto', padding: '20px 24px' }}>
        {/* Meal name + grade */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span data-testid="meal-grade" style={{
            width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700, color: '#fff', background: GRADE_COLORS[grade],
          }}>{grade}</span>
          <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 400, color: C.text, margin: 0 }} data-testid="meal-name">{meal.name}</h2>
        </div>
        {meal.why && <p style={{ fontSize: 13, color: C.textSec, margin: '4px 0 16px', lineHeight: 1.5, fontFamily: 'var(--font-sans)' }}>{meal.why}</p>}

        {/* Macro summary bar */}
        <div data-testid="macro-summary" style={{
          display: 'flex', gap: 8, marginBottom: 20, marginTop: meal.why ? 0 : 16,
        }}>
          {[
            { label: 'Calories', val: adjusted.calories, u: '', color: C.accent },
            { label: 'Protein', val: adjusted.protein, u: 'g', color: C.green },
            { label: 'Carbs', val: adjusted.carbs, u: 'g', color: C.gold },
            { label: 'Fat', val: adjusted.fat, u: 'g', color: '#D4736A' },
          ].map(m => (
            <div key={m.label} style={{ flex: 1, textAlign: 'center', padding: '12px 0', borderRadius: 14, background: `${m.color}08` }}>
              <p style={{ fontSize: 20, fontWeight: 700, color: m.color, margin: 0, fontFamily: 'var(--font-sans)' }}>{m.val}{m.u}</p>
              <p style={{ fontSize: 10, color: C.muted, margin: '2px 0 0' }}>{m.label}</p>
            </div>
          ))}
        </div>

        {/* Portion selector */}
        <div data-testid="portion-selector" style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: C.text, margin: '0 0 8px', fontFamily: 'var(--font-sans)' }}>Portion size</p>
          <div style={{ display: 'flex', gap: 6 }}>
            {PORTIONS.map(p => (
              <button
                key={p}
                data-testid={`portion-${p}`}
                onClick={() => setPortion(p)}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 12,
                  border: portion === p ? `2px solid ${C.accent}` : `1px solid ${C.border}`,
                  background: portion === p ? `${C.accent}10` : C.card,
                  color: portion === p ? C.accent : C.muted,
                  fontSize: 13, fontWeight: portion === p ? 700 : 500,
                  cursor: 'pointer', fontFamily: 'var(--font-sans)',
                }}
              >
                {p}x
              </button>
            ))}
          </div>
        </div>

        {/* Meal type selector */}
        <div data-testid="meal-type-selector" style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: C.text, margin: '0 0 8px', fontFamily: 'var(--font-sans)' }}>Meal type</p>
          <div style={{ display: 'flex', gap: 6 }}>
            {['breakfast', 'lunch', 'dinner', 'snack'].map(t => (
              <button
                key={t}
                data-testid={`meal-type-${t}`}
                onClick={() => setMealType(t)}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 12,
                  border: mealType === t ? `2px solid ${C.green}` : `1px solid ${C.border}`,
                  background: mealType === t ? `${C.green}10` : C.card,
                  color: mealType === t ? C.green : C.muted,
                  fontSize: 12, fontWeight: mealType === t ? 700 : 500,
                  cursor: 'pointer', fontFamily: 'var(--font-sans)', textTransform: 'capitalize',
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Ingredient list (if available) */}
        {items.length > 0 && (
          <div data-testid="ingredient-list" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: C.text, margin: 0, fontFamily: 'var(--font-sans)' }}>Ingredients</p>
              <p style={{ fontSize: 10, color: C.muted, margin: 0 }}>Tap to exclude</p>
            </div>
            <div style={{ borderRadius: 14, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
              {items.map((item, i) => {
                const excluded = excludedItems.has(i);
                return (
                  <button
                    key={i}
                    data-testid={`ingredient-${i}`}
                    onClick={() => setExcludedItems(prev => {
                      const next = new Set(prev);
                      if (next.has(i)) next.delete(i); else next.add(i);
                      return next;
                    })}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', background: excluded ? '#f5f5f0' : C.card,
                      border: 'none', borderBottom: i < items.length - 1 ? `1px solid ${C.border}` : 'none',
                      cursor: 'pointer', textAlign: 'left', opacity: excluded ? 0.5 : 1,
                      transition: 'opacity 0.15s',
                    }}
                  >
                    <div style={{
                      width: 20, height: 20, borderRadius: 6,
                      border: excluded ? 'none' : `1.5px solid ${C.border}`,
                      background: excluded ? C.muted : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {excluded && <Minus className="w-3 h-3" style={{ color: '#fff' }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{
                        fontSize: 13, color: C.text, margin: 0, fontFamily: 'var(--font-sans)',
                        textDecoration: excluded ? 'line-through' : 'none',
                      }}>
                        {item.name}{item.quantity ? ` — ${item.quantity}` : ''}
                      </p>
                    </div>
                    <span style={{ fontSize: 12, color: C.muted, fontFamily: 'var(--font-sans)', flexShrink: 0 }}>
                      {Math.round((item.calories || 0) * portion)} cal
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* View Full Recipe link */}
        {meal.recipe_id && (
          <button
            data-testid="view-recipe-link"
            onClick={() => navigate(`/recipe/${meal.recipe_id}`)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 0',
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, color: C.accent, fontWeight: 500, fontFamily: 'var(--font-sans)',
              marginBottom: 20,
            }}
          >
            <ChefHat className="w-4 h-4" /> View Full Recipe
          </button>
        )}

        {/* Confirm & Log CTA */}
        <button
          data-testid="confirm-log-btn"
          onClick={handleConfirm}
          disabled={logging || logged}
          style={{
            width: '100%', padding: '16px 0', borderRadius: 16,
            background: logged ? C.green : C.accent,
            color: '#fff', border: 'none', cursor: logging ? 'wait' : 'pointer',
            fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-sans)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'background 0.2s',
            boxShadow: `0 4px 12px ${logged ? C.green : C.accent}30`,
          }}
        >
          {logged ? <><Check className="w-5 h-5" /> Logged!</> : logging ? 'Logging...' : `Confirm & Log · ${adjusted.calories} cal`}
        </button>
      </main>
    </div>
  );
}
